import { useQuery } from '@/src/hooks/useQuery';
import { useApi } from '@/src/store/api.context';
import { useFlashbars } from '@/src/store/flashbar.context';
import { useJamEventDetails } from '@/src/store/jam-event-details.context';
import { EventTopicItem } from '@/src/types/EventTemplate';
import { FacilitatorMessagingFields } from '@/src/types/FacilitatorMessaging';
import { JamMinimalParticipant } from '@/src/types/JamEvent';
import { NotificationMessage, NotificationOptions } from '@/src/types/JamNotifications';
import {
  facilitatorMessagingValidator,
  MSG_CONTENT_MAX_LENGTH,
  MSG_TITLE_MAX_LENGTH,
} from '@/src/utils/facilitator-messaging.validations.utils';
import { i18nKeys } from '@/src/utils/i18n.utils';
import { FlashbarType } from '@/src/utils/notification.utils';
import {
  Box,
  Button,
  Checkbox,
  ColumnLayout,
  Container,
  ContentLayout,
  FormField,
  Header,
  Input,
  Modal,
  RadioGroup,
  Select,
  SpaceBetween,
  Textarea,
} from '@amzn/awsui-components-react';
import { OptionDefinition } from '@amzn/awsui-components-react/polaris/internal/components/option/interfaces';
import { uniq } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Divider from '../../ui/atoms/Divider/Divider';
import './FacilitatorMessaging.scss';

const RecipientOptions: EventTopicItem[] = [
  {
    label: i18nKeys.facilitator.messaging.form.fields.allParticipants,
    value: 'event',
  },
  {
    label: i18nKeys.facilitator.messaging.form.fields.specificTeam,
    value: 'team',
  },
  {
    label: i18nKeys.facilitator.messaging.form.fields.specificUser,
    value: 'participant',
  },
];

const MessageTypeOptions = [
  {
    label: i18nKeys.facilitator.messaging.form.fields.information,
    value: 'info',
  },
  {
    label: i18nKeys.facilitator.messaging.form.fields.warning,
    value: 'warning',
  },
];
const defaultAutoDismissSeconds = '5';

const FacilitatorMessaging = () => {
  const { t } = useTranslation();
  const { addFlashbar, clearFlashbars } = useFlashbars();
  const { jamFacilitatorApi, jamMessageApi } = useApi();
  const { eventName, event } = useJamEventDetails();
  const { queryParams } = useQuery();

  const recipientOptions = RecipientOptions.map((item) => ({ ...item, label: t(item.label) }));
  const messageTypeOptions = MessageTypeOptions.map((item) => ({ ...item, label: t(item.label) }));

  const [sending, setSending] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [autoDismiss, setAutoDismiss] = useState(true);
  const [recipient, setRecipient] = useState<OptionDefinition | null>(recipientOptions[0]);
  const [messageType, setMessageType] = useState<OptionDefinition | null>(messageTypeOptions[0]);
  const [title, setTitle] = useState(queryParams.get('title') || '');
  const [seconds, setSeconds] = useState(defaultAutoDismissSeconds);
  const [message, setMessage] = useState(queryParams.get('message') || '');
  const [notificationType, setNotificationType] = useState<'message' | 'email'>('message');
  const [allParticipants, setAllParticipants] = useState<JamMinimalParticipant[]>([]);
  const [selectedTeam, setSelecteTeam] = useState<OptionDefinition | null>();
  const [selectedParticipant, setSelectedParticipant] = useState<OptionDefinition | null>();

  const [titleError, setTitleError] = useState('');
  const [messageError, setMessageError] = useState('');
  const [teamError, setTeamError] = useState('');
  const [participantError, setParticipantError] = useState('');

  const secondsInNumber = seconds ? parseInt(seconds, 10) * 1000 : 0;

  const teamsList = useMemo(() => {
    const teams = uniq(allParticipants.filter(({ teamName }) => teamName).map(({ teamName }) => teamName));
    return teams.map((team) => ({ label: team, value: team }));
  }, [allParticipants]);

  const participantsList = useMemo(() => {
    return allParticipants.map(({ nickname, login, teamName }) => ({
      label: `${nickname} (${t(i18nKeys.jamTeam.title)}: ${teamName})`,
      value: login,
    }));
  }, [allParticipants]);

  const validator = useMemo(() => {
    const fields: [FacilitatorMessagingFields, (error: string) => void][] = [
      [FacilitatorMessagingFields.TITLE, (error: string) => setTitleError(error)],
      [FacilitatorMessagingFields.MESSAGE, (error: string) => setMessageError(error)],
    ];
    if (recipient?.value === 'team') {
      fields.push([FacilitatorMessagingFields.TEAM_NAME, (error: string) => setTeamError(error)]);
    }
    if (recipient?.value === 'participant') {
      fields.push([FacilitatorMessagingFields.PARTICIPANT_LOGIN, (error: string) => setParticipantError(error)]);
    }
    return facilitatorMessagingValidator(
      { title, message, teamName: selectedTeam?.value, participantLogin: selectedParticipant?.value },
      {
        title: t(i18nKeys.facilitator.messaging.form.errors.title, { num: MSG_TITLE_MAX_LENGTH }),
        message: t(i18nKeys.facilitator.messaging.form.errors.message, { num: MSG_CONTENT_MAX_LENGTH }),
        teamName: t(i18nKeys.facilitator.messaging.form.errors.teamName),
        participantLogin: t(i18nKeys.facilitator.messaging.form.errors.participantLogin),
      },
      new Map<FacilitatorMessagingFields, (error: string) => void>(fields)
    );
  }, [title, message, selectedParticipant, selectedTeam, messageType, recipient]);

  const getMessageType = () => {
    let type = messageType?.value;
    const emailNotif = notificationType === 'email';
    if (type === 'info' && emailNotif) {
      type = 'info_with_email';
    } else if (type === 'warning' && emailNotif) {
      type = 'warning_with_email';
    }
    return type;
  };

  const loadParticipants = async () => {
    if (!eventName) {
      return;
    }
    const response = await jamFacilitatorApi.getEventParticipants(eventName);
    setAllParticipants(response.participants);
  };

  const onSend = () => {
    clearFlashbars();
    if (!validator.isValidSection(true)) {
      return;
    }
    if (recipient?.value === 'event') {
      setShowConfirmation(true);
      return;
    }
    void sendMessage();
  };

  const sendMessage = async () => {
    if (!event || !validator.isValidSection(true) || !recipient) {
      return;
    }
    try {
      closeConfirmation();
      setSending(true);
      const notificationData = {
        notificationMessage: NotificationMessage.fromPlainObject({
          title: encodeURIComponent(title),
          message: encodeURIComponent(message),
          type: getMessageType(),
        }),
        options: NotificationOptions.fromPlainObject({ timeOut: autoDismiss ? secondsInNumber : 0 }),
      };

      switch (recipient.value) {
        case 'event':
          await jamMessageApi.sendMessageToEvent({
            eventName,
            notificationData,
          });
          break;
        case 'team':
          await jamMessageApi.sendMessageToTeam({
            eventName,
            notificationData,
            teamName: selectedTeam?.value,
          });
          break;
        case 'participant':
          await jamMessageApi.sendMessageToParticipant({
            eventName,
            notificationData,
            participantLogin: selectedParticipant?.value,
          });
          break;
      }
    } catch (e) {
      // error handled in API call already
    }
    setSending(false);
  };

  const closeConfirmation = () => {
    setShowConfirmation(false);
  };

  const onResetForm = () => {
    setAutoDismiss(false);
    setRecipient(recipientOptions[0]);
    setMessageType(messageTypeOptions[0]);
    setTitle('');
    setSeconds('');
    setMessage('');
    setNotificationType('message');
    setSelecteTeam(null);
    setSelectedParticipant(null);
  };

  const onPreviewNotification = () => {
    clearFlashbars();
    if (!validator.isValidSection(true)) {
      return;
    }
    const type = messageType?.value === 'info' ? FlashbarType.INFO : FlashbarType.WARNING;
    addFlashbar({
      id: `${new Date().getTime()}`,
      header: title,
      content: message,
      i18nKeys: [''],
      type,
      dismissible: true,
      autoDismiss,
      dismissDuration: secondsInNumber,
    });
  };

  useEffect(() => {
    if (!autoDismiss) {
      setSeconds(defaultAutoDismissSeconds);
    }
  }, [autoDismiss]);

  useEffect(() => {
    setSelecteTeam(null);
    setSelectedParticipant(null);
    void loadParticipants();
  }, [recipient]);

  return (
    <div className="facilitator-messaging">
      <ContentLayout header={<Header variant="h1">{t(i18nKeys.facilitator.messaging.title)}</Header>}>
        <Container>
          <ColumnLayout columns={2}>
            <FormField>
              <RadioGroup
                onChange={({ detail }) => setNotificationType(detail.value as 'message' | 'email')}
                value={notificationType}
                items={[
                  { value: 'message', label: t(i18nKeys.facilitator.messaging.form.fields.sendMessageNotif) },
                  { value: 'email', label: t(i18nKeys.facilitator.messaging.form.fields.sendEmailNotif) },
                ]}
                data-testid="facilitator-message-format-radiogroup"
              />
            </FormField>
            <div />
            <SpaceBetween size="xs">
              <FormField label={t(i18nKeys.facilitator.messaging.form.fields.recipient)}>
                <Select
                  selectedOption={recipient}
                  options={recipientOptions}
                  onChange={({ detail }) => setRecipient(detail.selectedOption)}
                  placeholder={t(i18nKeys.general.select)}
                  data-testid="facilitator-message-recipient-select"
                />
              </FormField>
              {recipient?.value === 'team' && (
                <FormField errorText={teamError} label={t(i18nKeys.facilitator.messaging.form.fields.teamName)}>
                  <Select
                    selectedOption={selectedTeam || null}
                    options={teamsList}
                    onChange={({ detail }) => setSelecteTeam(detail.selectedOption)}
                    placeholder={t(i18nKeys.general.select)}
                    data-testid="facilitator-message-recipient-team-select"
                  />
                </FormField>
              )}
              {recipient?.value === 'participant' && (
                <FormField
                  errorText={participantError}
                  label={t(i18nKeys.facilitator.messaging.form.fields.participant)}
                >
                  <Select
                    selectedOption={selectedParticipant || null}
                    options={participantsList}
                    onChange={({ detail }) => setSelectedParticipant(detail.selectedOption)}
                    data-testid="facilitator-message-recipient-participant-select"
                  />
                </FormField>
              )}
            </SpaceBetween>
            <FormField label={t(i18nKeys.facilitator.messaging.form.fields.messageType)}>
              <Select
                selectedOption={messageType}
                options={messageTypeOptions}
                onChange={({ detail }) => setMessageType(detail.selectedOption)}
                data-testid="facilitator-message-type-select"
              />
            </FormField>
            <FormField
              errorText={titleError}
              label={t(i18nKeys.facilitator.messaging.form.fields.title)}
              constraintText={`${title.length}/${MSG_TITLE_MAX_LENGTH}`}
            >
              <Input
                data-testid="broadcastTitleTextBox"
                value={title}
                onChange={({ detail }) => setTitle(detail.value)}
              />
            </FormField>
            <FormField>
              <Checkbox
                disabled={notificationType === 'email'}
                onChange={({ detail }) => setAutoDismiss(detail.checked)}
                checked={autoDismiss}
                data-testid="facilitator-message-autodismiss-checkbox"
              >
                {t(i18nKeys.facilitator.messaging.form.fields.autoDismiss)}
              </Checkbox>

              <div className="dismiss-seconds">
                <Input
                  ariaLabel={t(i18nKeys.facilitator.messaging.form.fields.autoDismiss)}
                  disabled={!autoDismiss || notificationType === 'email'}
                  type="number"
                  value={seconds}
                  onChange={({ detail }) => setSeconds(detail.value)}
                  data-testid="facilitator-message-audodismiss-duration-input"
                />{' '}
                {t(i18nKeys.facilitator.messaging.form.fields.seconds)}
              </div>
            </FormField>
            <FormField
              errorText={messageError}
              label={t(i18nKeys.facilitator.messaging.form.fields.message)}
              constraintText={`${message.length}/${MSG_CONTENT_MAX_LENGTH}`}
            >
              <Textarea
                data-testid="broadcastMessageTextBox"
                ariaLabel={t(i18nKeys.facilitator.messaging.form.fields.message)}
                value={message}
                onChange={({ detail }) => setMessage(detail.value)}
              />
            </FormField>
          </ColumnLayout>
          <Box margin={{ top: 'l', bottom: 'l' }}>
            <Divider />
          </Box>
          <div className="actions">
            <Button iconName="refresh" onClick={onResetForm} data-testid="facilitator-message-form-reset-btn">
              {t(i18nKeys.facilitator.messaging.form.actions.reset)}
            </Button>
            <SpaceBetween direction="horizontal" size="s">
              <Button disabled={sending} onClick={onPreviewNotification} data-testid="facilitator-message-preview-btn">
                {t(i18nKeys.facilitator.messaging.form.actions.preview)}
              </Button>
              <Button
                variant="primary"
                disabled={sending}
                loading={sending}
                id="sendBroadCastMessageButton"
                data-testid="sendBroadCastMessageButton"
                onClick={onSend}
              >
                {t(
                  i18nKeys.facilitator.messaging.form.actions[
                    notificationType === 'message' ? 'sendMessage' : 'sendEmail'
                  ]
                )}
              </Button>
            </SpaceBetween>
          </div>
        </Container>
      </ContentLayout>
      <Modal
        onDismiss={closeConfirmation}
        visible={showConfirmation}
        header={<Header>{t(i18nKeys.facilitator.messaging.form.modal.sendConfirm.title)}</Header>}
        footer={
          <SpaceBetween direction="horizontal" size="s">
            <Button onClick={closeConfirmation}>{t(i18nKeys.general.cancel)}</Button>
            <Button onClick={() => void sendMessage()} data-testid="facilitator-send-message-btn">
              {t(i18nKeys.general.send)}
            </Button>
          </SpaceBetween>
        }
        data-testid="facilitator-send-message-modal"
      >
        {t(i18nKeys.facilitator.messaging.form.modal.sendConfirm.caption)}
      </Modal>
    </div>
  );
};

export default FacilitatorMessaging;
